/**
 * Booking UI theme tokens & helpers.
 *
 * Controls every visual token used by booking components — colors, borders,
 * radii, spacing, typography, avatars.  Switching between light and dark mode
 * (or any custom palette) is a single-prop affair: pass the desired
 * `BookingTheme` (or `"light" | "dark"`) to `<BookingThemeRoot>` and every
 * descendant picks up the new values through CSS custom properties.
 *
 * ## How it works
 *
 * 1.  `BookingTheme` — plain JS object with every design token.
 * 2.  `bookingLightTheme` / `bookingDarkTheme` — built-in presets.
 * 3.  `buildBookingCssVars(theme)` — converts a theme into a `CSSProperties`
 *     record of `--openings-booking-*` custom properties that can be spread
 *     onto a wrapper `<div style={…}>`.
 * 4.  All shared booking styles in `bookingStyles.ts` reference these CSS
 *     custom properties with sensible fallbacks so they work even without
 *     an explicit theme wrapper.
 *
 * The wrapper component (`BookingThemeRoot`) lives in the same file so apps
 * can import it alongside the presets without an extra module.
 */
import type { CSSProperties } from "react";

/* ────────────────────────────────────────────────────────────
 * 1 · Token type
 * ──────────────────────────────────────────────────────────── */

export type BookingThemeMode = "light" | "dark";

export type BookingTheme = {
  /* ── Colors ── */
  /** Card / panel background */
  colorSurface: string;
  /** Page / section background */
  colorBackground: string;
  /** All borders */
  colorBorder: string;
  /** Primary text */
  colorText: string;
  /** Muted / secondary text (replaces per-element opacity hacks) */
  colorTextSecondary: string;
  /** Brand / accent */
  colorPrimary: string;
  /** Text on top of primary background */
  colorPrimaryText: string;

  /* ── Border ── */
  /** Default radius — cards, panels, inputs */
  borderRadius: number;
  /** Smaller radius — time buttons, thumbnails */
  borderRadiusSm: number;
  /** Border thickness */
  borderWidth: number;

  /* ── Typography ── */
  /** Schedule/section heading size (e.g. selected-schedule title) */
  fontSizeLg: number;
  fontSizeBase: number;
  fontSizeSm: number;
  fontSizeXs: number;
  fontWeightNormal: number;
  fontWeightMedium: number;
  fontWeightSemibold: number;

  /* ── Spacing ── */
  spacingXs: number;
  spacingSm: number;
  spacingMd: number;
  spacingLg: number;
  spacingXl: number;

  /* ── Avatar ── */
  avatarSize: number;
  avatarSizeSm: number;
};

/* ────────────────────────────────────────────────────────────
 * 2 · Presets
 * ──────────────────────────────────────────────────────────── */

export const bookingLightTheme: BookingTheme = {
  colorSurface: "#ffffff",
  colorBackground: "#ffffff",
  colorBorder: "#e5e5e5",
  colorText: "#111111",
  colorTextSecondary: "#666666",
  colorPrimary: "#111111",
  colorPrimaryText: "#ffffff",

  borderRadius: 10,
  borderRadiusSm: 6,
  borderWidth: 1,

  fontSizeLg: 16,
  fontSizeBase: 14,
  fontSizeSm: 13,
  fontSizeXs: 12,
  fontWeightNormal: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,

  spacingXs: 4,
  spacingSm: 8,
  spacingMd: 12,
  spacingLg: 16,
  spacingXl: 24,

  avatarSize: 36,
  avatarSizeSm: 32,
};

export const bookingDarkTheme: BookingTheme = {
  colorSurface: "#111111",
  colorBackground: "#111111",
  colorBorder: "#2a2a2a",
  colorText: "#f5f5f5",
  colorTextSecondary: "#999999",
  colorPrimary: "#ffffff",
  colorPrimaryText: "#111111",

  borderRadius: 10,
  borderRadiusSm: 6,
  borderWidth: 1,

  fontSizeLg: 16,
  fontSizeBase: 14,
  fontSizeSm: 13,
  fontSizeXs: 12,
  fontWeightNormal: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,

  spacingXs: 4,
  spacingSm: 8,
  spacingMd: 12,
  spacingLg: 16,
  spacingXl: 24,

  avatarSize: 36,
  avatarSizeSm: 32,
};

/** Resolve a mode string or custom theme object to a `BookingTheme`. */
export function resolveBookingTheme(
  input?: BookingThemeMode | BookingTheme | null,
): BookingTheme {
  if (!input || input === "light") return bookingLightTheme;
  if (input === "dark") return bookingDarkTheme;
  return input;
}

/* ────────────────────────────────────────────────────────────
 * 3 · CSS custom-property builder
 * ──────────────────────────────────────────────────────────── */

/**
 * Converts a `BookingTheme` into CSS custom properties that can be applied
 * as inline styles on a wrapper element.  Every booking component reads
 * these variables (with fallbacks) so the entire subtree reacts.
 */
export function buildBookingCssVars(theme: BookingTheme): CSSProperties {
  return {
    /* Colors */
    "--openings-color-surface": theme.colorSurface,
    "--openings-color-background": theme.colorBackground,
    "--openings-color-border": theme.colorBorder,
    "--openings-color-text": theme.colorText,
    "--openings-color-text-secondary": theme.colorTextSecondary,
    "--openings-color-primary": theme.colorPrimary,
    "--openings-color-primary-text": theme.colorPrimaryText,

    /* Border */
    "--openings-booking-radius": `${theme.borderRadius}px`,
    "--openings-booking-radius-sm": `${theme.borderRadiusSm}px`,
    "--openings-booking-border-width": `${theme.borderWidth}px`,

    /* Typography */
    "--openings-booking-font-size-lg": `${theme.fontSizeLg}px`,
    "--openings-booking-font-size": `${theme.fontSizeBase}px`,
    "--openings-booking-font-size-sm": `${theme.fontSizeSm}px`,
    "--openings-booking-font-size-xs": `${theme.fontSizeXs}px`,
    "--openings-booking-font-weight-normal": String(theme.fontWeightNormal),
    "--openings-booking-font-weight-medium": String(theme.fontWeightMedium),
    "--openings-booking-font-weight-semibold": String(theme.fontWeightSemibold),

    /* Spacing */
    "--openings-booking-spacing-xs": `${theme.spacingXs}px`,
    "--openings-booking-spacing-sm": `${theme.spacingSm}px`,
    "--openings-booking-spacing-md": `${theme.spacingMd}px`,
    "--openings-booking-spacing-lg": `${theme.spacingLg}px`,
    "--openings-booking-spacing-xl": `${theme.spacingXl}px`,

    /* Avatar */
    "--openings-booking-avatar-size": `${theme.avatarSize}px`,
    "--openings-booking-avatar-size-sm": `${theme.avatarSizeSm}px`,

    /* Root text color so all descendants inherit */
    color: theme.colorText,
  } as unknown as CSSProperties;
}
